import { memo, useRef } from 'react';

import { useAriaId } from '@components/component-helpers';
import { UploadedFile } from '@components/file-field';
import { FileAcceptText } from '@components/file-field/file-accept-text';
import {
  InstantUploadFileInput,
  InstantUploadFileInputProps,
  useInstantFile,
} from '@components/file-field/file-input';
import { FileInputContent } from '@components/file-field/file-input-content';
import { FileFieldBaseProps } from '@components/file-field/types';
import { FormError } from '@components/form-error';
import { FormLabelContainer } from '@components/form-label/form-label';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';

export type InstantUploadFileFieldProps = Omit<InstantUploadFileInputProps, 'setFile'> &
  FileFieldBaseProps;

export const InstantUploadFileField = memo<InstantUploadFileFieldProps>(
  ({
    label,
    hideLabel,
    accept,
    fileType,
    error,
    placeholder: userPlaceholder,
    onChange,
    onSelect,
    defaultValue,
    isDisabled,
  }) => {
    const { t } = useScopedTranslate(locale);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const inputId = useAriaId('file');
    const [file, setFile] = useInstantFile(defaultValue);

    const placeholder = userPlaceholder ?? t('placeholder');

    return (
      <>
        <Stack space={1}>
          <FormLabelContainer id={inputId} label={label} hideLabel={hideLabel} />
          <InstantUploadFileInput
            isDisabled={isDisabled}
            id={inputId}
            ref={fileInputRef}
            accept={accept}
            fileType={fileType}
            onChange={onChange}
            onSelect={onSelect}
            setFile={setFile}
          />

          {file ? (
            <UploadedFile
              isDisabled={isDisabled}
              name={file.name}
              onRemove={() => {
                setFile(null);
                onChange(null);
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
              }}
            />
          ) : (
            <FileInputContent
              isDisabled={isDisabled}
              error={error}
              placeholder={placeholder}
              onClick={() => fileInputRef.current?.click()}
            />
          )}

          <FileAcceptText accept={accept} />
          <FormError text={error} />
        </Stack>
      </>
    );
  }
);

const locale = {
  en: {
    placeholder: 'Upload file',
    accepted: 'Accepted file types:',
  },
  is: {
    placeholder: 'Bæta við skrá',
    accepted: 'Leyfðar skráartegundir:',
  },
  dk: {
    placeholder: 'Vælg fil',
    accepted: 'Accepterede filtyper:',
  },
};
